import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { twMerge } from 'tailwind-merge';
import Labeled from '../../../components/misc/Labeled';
import Input from '../../../components/misc/Input';
import DropdownSelect from '../../../components/forms/DropdownSelect';
import DatePicker from '../../../components/forms/DatePicker';
import CurrencyInput from '../../../components/forms/CurrencyInput';
import ConnectionsList from '../../../components/misc/ConnectionsList';
import Button from '../../../components/buttons/Button';
import { RootState } from '../../../../redux/reducers';
import { ConnectionType } from '../../../../redux/slices/connectionsSlice';
import { ContributionFrequency, CreateContributionPlanDto } from '../../../../types/bankAccount';

type ContributionPlanFormWizardProps = {
  mode: 'create' | 'edit';
  initialValue: CreateContributionPlanDto;
  onSubmit: (dto: CreateContributionPlanDto) => Promise<void>;
};

export default function ContributionPlanFormWizard({
  mode,
  initialValue,
  onSubmit,
}: ContributionPlanFormWizardProps): JSX.Element {
  const { t, i18n } = useTranslation('', {
    keyPrefix: 'page.financial.contributionPlans.createModal',
  });
  const { t: tEnum } = useTranslation('', { keyPrefix: 'page.financial.enums' });

  const _connections = useSelector((state: RootState) => state.connections);
  const connections = useMemo(
    () => _connections.filter((c) => c.type === ConnectionType.MEMBER || c.type === ConnectionType.EXTERNAL_MEMBER),
    [_connections],
  );

  const [step, setStep] = useState<1 | 2>(1);
  const [form, setForm] = useState<CreateContributionPlanDto>(initialValue);
  const [installmentAmountCents, setInstallmentAmountCents] = useState(0);
  const [calcPulse, setCalcPulse] = useState(false);

  useEffect(() => {
    if (form.frequency === ContributionFrequency.ONCE) {
      setForm((prev) => ({ ...prev, numberOfInstallments: 1 }));
    }
  }, [form.frequency]);

  useEffect(() => {
    setCalcPulse(true);
    const id = setTimeout(() => setCalcPulse(false), 180);
    return () => clearTimeout(id);
  }, [installmentAmountCents, form.numberOfInstallments, form.frequency]);

  const installmentsEffective =
    form.frequency === ContributionFrequency.ONCE ? 1 : form.numberOfInstallments;
  const computedTotalCents = (installmentAmountCents || 0) * (installmentsEffective || 0);

  const isStep1Valid = useMemo(() => {
    if (!form.name || form.name.trim().length === 0) return false;
    if (!form.startDate) return false;
    if (!Number.isFinite(installmentAmountCents) || installmentAmountCents <= 0) return false;
    if (!Number.isFinite(installmentsEffective) || installmentsEffective < 1) return false;
    return true;
  }, [form.name, form.startDate, installmentAmountCents, installmentsEffective]);

  const isStep2Valid = useMemo(() => {
    return Array.isArray(form.appliesToDashboardMemberId) && form.appliesToDashboardMemberId.length > 0;
  }, [form.appliesToDashboardMemberId]);

  const handleChange = <K extends keyof CreateContributionPlanDto>(name: K, value: CreateContributionPlanDto[K]) => {
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleNext = () => {
    if (step === 1 && isStep1Valid) setStep(2);
  };

  const handleBack = () => {
    if (step === 2) setStep(1);
  };

  const handleSubmit = async () => {
    const effectiveInstallments =
      form.frequency === ContributionFrequency.ONCE ? 1 : form.numberOfInstallments;
    const totalCents = installmentAmountCents * effectiveInstallments;
    await onSubmit({ ...form, amount: totalCents });
  };

  return (
    <div className="flex flex-col gap-4">
      {/* Step progress bar (similar to CreateCommunicationPage) */}
      <div className="mx-auto flex h-2 w-full max-w-xl gap-6">
        {[0, 1].map((i) => (
          <div
            key={i}
            onClick={i < step - 1 ? () => setStep((i + 1) as 1 | 2) : undefined}
            className={`h-2 w-full rounded-[5px] bg-primary-900 transition-all ${i > step - 1 ? 'bg-opacity-15' : 'cursor-pointer'}`}
          />
        ))}
      </div>

      {step === 1 && (
        <div className="flex flex-col gap-2">
          <Input
            id="plan-name"
            type="text"
            label={t('name')}
            placeholder={t('namePlaceholder')}
            onChange={(e) => handleChange('name', e.target.value)}
            value={form.name}
          />
          <Labeled label={t('description')} htmlFor="plan-description">
            <textarea
              id="plan-description"
              rows={2}
              className="h-[unset]"
              placeholder={t('descriptionPlaceholder')}
              onChange={(e) => handleChange('description', e.target.value)}
              value={form.description}
            />
          </Labeled>
          <Labeled label={t('frequency')}>
            <DropdownSelect
              options={Object.values(ContributionFrequency).map((freq) => ({
                value: freq,
                label: tEnum(`ContributionFrequency.${freq}`),
              }))}
              id="plan-frequency"
              value={form.frequency}
              setValue={(value) => value !== undefined && handleChange('frequency', value)}
              placeholder={t('amountPlaceholder')}
            />
          </Labeled>
          <div className="flex items-center gap-2">
            <Labeled label={t('startDate')}>
              <DatePicker
                date={form.startDate}
                setDate={(date) => date !== undefined && handleChange('startDate', date)}
                includeTime={false}
              />
            </Labeled>
            <Labeled label={t('numberOfInstallments')}>
              <Input
                value={form.numberOfInstallments.toFixed(0)}
                type="number"
                className="disabled:opacity-50"
                min={1}
                disabled={form.frequency === ContributionFrequency.ONCE}
                onChange={(e) => handleChange('numberOfInstallments', parseInt(e.target.value, 10))}
                placeholder={t('numberOfInstallmentsPlaceholder')}
              />
            </Labeled>
          </div>
          <CurrencyInput
            id="plan-installment-amount"
            type="text"
            label={t('amount')}
            debounced
            handleChange={(value) => setInstallmentAmountCents(Math.floor(value * 100))}
          />
          <p
            className={twMerge(
              'text-sm text-gray-600 transition-transform duration-200 ease-out',
              calcPulse ? 'scale-[1.04]' : 'scale-100',
            )}>
            {`${(installmentAmountCents / 100).toLocaleString(i18n.language, { style: 'currency', currency: 'EUR' })} × ${installmentsEffective} = ${(computedTotalCents / 100).toLocaleString(i18n.language, { style: 'currency', currency: 'EUR' })}`}
          </p>

          <div className="mt-2 flex justify-end gap-2">
            <Button variant="primary-dark" disabled={!isStep1Valid} onClick={handleNext}>
              {t('next')}
            </Button>
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="flex flex-col gap-2">
          <p className="font-medium">{t('appliesToExplanation')}</p>
          <ConnectionsList
            connections={connections.map((c) => ({
              id: c.dashboardMemberId,
              alias: `${c.fields.FIRST_NAME} ${c.fields.LAST_NAME}`,
              picture: c.picture,
            }))}
            selected={form.appliesToDashboardMemberId}
            setSelected={(ids) => handleChange('appliesToDashboardMemberId', ids)}
          />

          <div className="mt-2 flex justify-between gap-2">
            <Button variant="secondary" onClick={handleBack}>
              {t('back')}
            </Button>
            <Button variant="primary-dark" disabled={!isStep2Valid} onClick={handleSubmit}>
              {mode === 'create'
                ? t('createButton')
                : t('saveChanges', { defaultValue: 'Save changes' })}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
